"use client";

import { useEffect, useRef } from "react";

const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const SCRIPT_ID = "cf-turnstile-script";

// The script is fetched from challenges.cloudflare.com, which private-mode
// browsers, content blockers and filtering DNS all block. A blocked request can
// hang rather than fail, so the load is raced against a deadline — without it
// the widget never renders and never reports, leaving the consumer stuck on
// "not solved yet" forever.
const SCRIPT_TIMEOUT_MS = 10_000;

/** Why the widget could not produce a token. */
export type TurnstileErrorReason =
  /** The Cloudflare script failed to load, timed out, or was blocked. */
  | "script-unavailable"
  /** The widget rendered but the challenge itself failed. */
  | "challenge-failed"
  /** A previously issued token expired and must be re-solved. */
  | "token-expired";

interface TurnstileApi {
  remove(widgetId: string): void;
  render(
    element: HTMLElement,
    options: {
      callback: (token: string) => void;
      "error-callback": () => void;
      "expired-callback": () => void;
      sitekey: string;
      theme?: "auto" | "dark" | "light";
    }
  ): string;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

// Loads the Turnstile script once per page, no matter how many widgets mount.
// Rejects when the script cannot be loaded, so the caller can surface it.
function loadScript(): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.resolve();
  }
  if (window.turnstile) {
    return Promise.resolve();
  }

  const settled = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener(
        "error",
        () => reject(new Error("Turnstile script failed to load")),
        { once: true }
      );
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.addEventListener("load", () => resolve(), { once: true });
    script.addEventListener(
      "error",
      () => reject(new Error("Turnstile script failed to load")),
      { once: true }
    );
    document.head.appendChild(script);
  });

  const deadline = new Promise<void>((_, reject) => {
    setTimeout(
      () => reject(new Error("Turnstile script load timed out")),
      SCRIPT_TIMEOUT_MS
    );
  });

  return Promise.race([settled, deadline]);
}

/**
 * Renders an invisible-by-default Turnstile widget and reports its token.
 *
 * The token is single-use and expires (~5 minutes), so `onToken` fires again on
 * refresh and the consumer must always send the most recent value. On error or
 * expiry the token is cleared to null, which the server then rejects — the
 * widget never silently degrades to "no challenge".
 *
 * `onError` fires alongside the null token whenever a token cannot be produced.
 * Without it a consumer cannot tell "not solved yet" from "will never solve",
 * and a disabled submit button looks identical in both cases.
 *
 * Renders nothing when no site key is configured, so local dev without
 * Turnstile secrets behaves exactly as before.
 */
export function TurnstileWidget({
  onError,
  onToken,
  siteKey,
  theme = "auto",
}: {
  onError?: (reason: TurnstileErrorReason) => void;
  onToken: (token: string | null) => void;
  siteKey: string;
  theme?: "auto" | "dark" | "light";
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  // Kept in refs so the effect never re-runs when the parent re-renders with a
  // new callback identity, which would tear down and re-render the widget.
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    if (!siteKey) {
      return;
    }

    let widgetId: string | undefined;
    let cancelled = false;

    const fail = (reason: TurnstileErrorReason) => {
      if (cancelled) {
        return;
      }
      onTokenRef.current(null);
      onErrorRef.current?.(reason);
    };

    loadScript()
      .then(() => {
        if (cancelled) {
          return;
        }
        if (!(containerRef.current && window.turnstile)) {
          fail("script-unavailable");
          return;
        }
        widgetId = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          callback: (token: string) => {
            if (!cancelled) {
              onTokenRef.current(token);
            }
          },
          "error-callback": () => fail("challenge-failed"),
          "expired-callback": () => fail("token-expired"),
        });
      })
      .catch(() => fail("script-unavailable"));

    return () => {
      cancelled = true;
      if (widgetId && window.turnstile) {
        window.turnstile.remove(widgetId);
      }
    };
  }, [siteKey, theme]);

  if (!siteKey) {
    return null;
  }

  return <div ref={containerRef} />;
}
